import { PageLayout } from '@app/pages/layout/PageLayout'
import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import { useLocation, useSearch } from 'wouter'
import { KnowledgeHubModule } from '@schema/knowledgeHubModule'
import { LeftSidebar } from '@app/components/layout/LeftSidebar'
import { KnowledgeHubModuleSidebar } from './KnowledgeHubModuleSidebar'
import { KnowledgeHubModuleContent } from './KnowledgeHubModuleContent'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import {
  linkStyle,
  linkSizeStyle,
  linkIconSizeStyle,
} from '@app/components/core/link/linkStyleHeper'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { PageSeo } from '@app/components/core/seo/PageSeo'

const BackLink = styled.a

export type KnowledgeHubItemPageProps = {
  itemId: string
}

export const KnowledgeHubModulePage = ({
  itemId,
}: KnowledgeHubItemPageProps) => {
  const t = useTranslations().app.knowledgeHubPage
  const [, setLocation] = useLocation()
  const knowledgeHubModuleQuery = useQuery('view-knowledge-hub-module', {
    id: itemId,
  })
  const knowledgeHubModuleItem: KnowledgeHubModule | undefined =
    knowledgeHubModuleQuery.data
  const search = useSearch()
  const itemSlug = useMemo(
    () =>
      new URLSearchParams(search).get('slug') ||
      knowledgeHubModuleItem?.sections?.[0]?.knowledgeHubItems?.[0]?.slug,
    [search, knowledgeHubModuleItem],
  )
  const itemQuery = useQuery(
    itemSlug ? 'view-knowledge-hub-item' : null,
    itemSlug ? { id: itemSlug } : undefined,
  )

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (window.history.length > 1) {
      window.history.back()
    } else {
      setLocation(pages.knowledgeHub)
    }
  }

  const modulePageStatus = getEntityPageStatus(
    knowledgeHubModuleQuery.loading,
    knowledgeHubModuleItem,
  )

  if (modulePageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (modulePageStatus === 'loading') {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        {t.loading}
      </PageLayout>
    )
  }

  if (itemSlug) {
    const itemPageStatus = getEntityPageStatus(itemQuery.loading, itemQuery.data)
    if (itemPageStatus === 'not-found') {
      return <NotFoundPage />
    }
    if (itemPageStatus === 'loading') {
      return (
        <PageLayout gap="2rem">
          <PageSeo />
          {t.loading}
        </PageLayout>
      )
    }
  }

  return (
    <>
      <PageLayout gap="0rem">
        <LeftSidebar>
          <LeftSidebar.Sidebar collapsedButtonLabel={t.menu}>
            <KnowledgeHubModuleSidebar
              sections={knowledgeHubModuleItem.sections}
              activeSlug={itemSlug}
            />
          </LeftSidebar.Sidebar>
          <div>
            <BackLink
              href={pages.knowledgeHub}
              onClick={handleBackClick}
              style={linkStyle}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={linkIconSizeStyle.medium}
              />
              <Text.Medium style={linkSizeStyle.medium}>{t.back}</Text.Medium>
            </BackLink>
            <KnowledgeHubModuleContent
              id={itemSlug || ''}
              moduleTitle={knowledgeHubModuleItem.title || ''}
            />
          </div>
        </LeftSidebar>
      </PageLayout>
    </>
  )
}
